import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Users, Heart, Sparkles, Radio } from 'lucide-react';
import { donationDashboard } from '../mock';

const STORAGE_KEY = 'acci_live_dashboard_v1';
const TICK_MS = 4500; // pace of simulated donations
const AMOUNT_POOL = [5, 5, 10, 10, 15, 20, 25, 30, 50, 75];

// Client-side "live" dashboard.
// Uses localStorage to persist a rolling total across reloads,
// then keeps ticking upward with small, realistic donations so the
// counter feels alive without needing a backend.
const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.raised === 'number' && typeof parsed.donors === 'number') {
        return parsed;
      }
    }
  } catch (_) { /* fall through */ }
  return {
    raised: donationDashboard.baseRaised,
    donors: donationDashboard.baseDonors,
    lastAmount: null,
    lastAt: null,
  };
};

const formatGBP = (n) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const LiveDonationDashboard = () => {
  const [state, setState] = useState(loadState);
  const [pulse, setPulse] = useState(false);
  const [displayRaised, setDisplayRaised] = useState(state.raised);
  const rafRef = useRef(null);

  // Persist any state change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Simulated live donation heartbeat
  useEffect(() => {
    const id = setInterval(() => {
      const amount = AMOUNT_POOL[Math.floor(Math.random() * AMOUNT_POOL.length)];
      setState((prev) => ({
        raised: prev.raised + amount,
        donors: prev.donors + 1,
        lastAmount: amount,
        lastAt: Date.now(),
      }));
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Smooth counter animation
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = displayRaised;
    const to = state.raised;
    const dur = 900;
    const step = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayRaised(Math.floor(from + (to - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state.raised]);

  const goal = donationDashboard.goal;
  const pct = Math.min((state.raised / goal) * 100, 100);

  return (
    <section
      id="live-impact"
      data-testid="live-impact-dashboard"
      className="relative py-24 lg:py-28 bg-[#0E1112] text-white overflow-hidden"
    >
      {/* Soft brand glow */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#6e12ed] opacity-25 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 w-[420px] h-[420px] rounded-full bg-[#79c4c7] opacity-25 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#79c4c7] mb-5">
              <Radio className="w-3.5 h-3.5" />
              Live Impact Dashboard
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl font-bold leading-[1.02]">
              Every stand is <span className="hl-box hl-purple">counted</span>,<br />
              in real time.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/70 text-lg leading-relaxed">
              Watch the campaign move as it happens. Each pulse is a person, a family, a stand for
              culturally responsive mental health care in the Black Country.
            </p>
          </div>
        </div>

        {/* Main ticker card */}
        <div
          data-testid="live-impact-card"
          className="rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur-md p-8 lg:p-10 shadow-[0_10px_60px_rgba(0,0,0,0.35)]"
        >
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 text-sm text-white/60 mb-3">
                <span
                  className={`inline-flex w-2.5 h-2.5 rounded-full bg-[#79c4c7] ${
                    pulse ? 'animate-ping' : ''
                  }`}
                  aria-hidden
                />
                <span className="inline-flex w-2.5 h-2.5 rounded-full bg-[#79c4c7] -ml-4" aria-hidden />
                Raised so far · Annual campaign
              </div>

              <div
                data-testid="live-impact-raised"
                className={`font-serif font-bold leading-none tracking-tight transition-colors duration-500 ${
                  pulse ? 'text-[#79c4c7]' : 'text-white'
                }`}
                style={{ fontSize: 'clamp(3rem, 8vw, 6rem)' }}
              >
                {formatGBP(displayRaised)}
              </div>
              <div className="mt-2 text-white/60 text-sm">
                of <span className="text-white font-semibold">{formatGBP(goal)}</span> goal
              </div>

              {/* Progress bar */}
              <div className="mt-6 h-3 rounded-full bg-white/10 overflow-hidden">
                <div
                  data-testid="live-impact-progress"
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{
                    width: `${pct}%`,
                    background: 'linear-gradient(90deg, #6e12ed 0%, #79c4c7 100%)',
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-white/50 tracking-wide">
                {pct.toFixed(1)}% of goal reached
              </div>

              <a
                href="#donate"
                data-testid="live-impact-cta"
                className="mt-8 inline-flex items-center gap-2 bg-[#6e12ed] hover:bg-[#5A0DCC] text-white font-semibold text-sm px-7 h-12 rounded-md transition-colors"
              >
                <Heart className="w-4 h-4 fill-white" />
                Add your stand
              </a>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              <StatTile
                label="Donors this year"
                value={state.donors.toLocaleString()}
                icon={Users}
                tone="aqua"
                testid="live-donor-count"
              />
              <StatTile
                label="Hours of counselling"
                value={donationDashboard.hoursCounselling.toLocaleString()}
                icon={Sparkles}
                tone="purple"
              />
              <StatTile
                label="Meals delivered"
                value={donationDashboard.mealsDelivered.toLocaleString()}
                icon={Heart}
                tone="purple"
              />
              <StatTile
                label="Momentum"
                value={state.lastAmount ? `+£${state.lastAmount}` : '£—'}
                sub={state.lastAt ? 'just now' : 'awaiting first stand'}
                icon={TrendingUp}
                tone="aqua"
                pulse={pulse}
                testid="live-last-donation"
              />
            </div>
          </div>
        </div>

        <p className="text-xs text-white/40 mt-4">
          Demo mode · counter animates client-side until the donations API is wired.
        </p>
      </div>
    </section>
  );
};

const StatTile = ({ label, value, sub, icon: Icon, tone, pulse, testid }) => {
  const color = tone === 'aqua' ? '#79c4c7' : '#6e12ed';
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl p-5 border border-white/10 bg-white/[0.03] transition-colors ${
        pulse ? 'ring-1 ring-[#79c4c7]' : ''
      }`}
    >
      <div className="flex items-center gap-2 text-white/60 text-[11px] uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div
        className="mt-2 font-serif text-3xl lg:text-4xl font-bold leading-none"
        style={{ color }}
      >
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-white/45">{sub}</div>}
    </div>
  );
};

export default LiveDonationDashboard;
